import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  KeyRound, Plus, Copy, Check, AlertTriangle, Ban, Server, Globe, X, RefreshCw,
  ShieldOff, Trash2,
} from 'lucide-react';
import { benutzerSchluessel, servers as serversApi, type BenutzerSchluessel } from '../api';
import SchluesselAnleitung from './SchluesselAnleitung';

/**
 * SSH-Zugang eines Menschen: Schlüssel, ausgeschlossene Server, Adressfreigaben.
 *
 * ══ DER PRIVATE SCHLÜSSEL WIRD EINMAL GEZEIGT ══════════════════════════════
 *
 * Danach ist er fort — im PC steht nur der öffentliche Teil. Deshalb steht die
 * Anleitung direkt daneben und nicht hinter einem Link: wer in diesem Moment
 * erst suchen muss, wohin die Datei gehört, legt sie irgendwo ab.
 *
 * ══ VERTEILT WIRD ÜBER authorized_keys ═════════════════════════════════════
 *
 * Nicht über die Anbieter-APIs — die setzen Schlüssel nur beim Erstellen einer
 * Maschine. Der PC schreibt in einen markierten Block in `~/.ssh/authorized_keys`
 * und lässt alles andere dort unberührt.
 */

function Zeitpunkt({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-slate-400">—</span>;
  return <span>{new Date(iso).toLocaleDateString('de-DE')}</span>;
}

/** Der frisch erzeugte Schlüssel — einmalig, mit Anleitung. */
function SchluesselAnzeige({
  privaterSchluessel,
  onFertig,
}: {
  privaterSchluessel: string;
  onFertig: () => void;
}) {
  const [kopiert, setKopiert] = useState(false);
  const [verstanden, setVerstanden] = useState(false);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-5 py-3">
          <h2 className="flex items-center gap-1.5 text-base font-semibold text-amber-900">
            <AlertTriangle size={16} /> Einmalige Anzeige
          </h2>
          <p className="mt-0.5 text-xs text-amber-800">
            Dieser Schlüssel wird nirgends gespeichert. Sobald du dieses Fenster schliesst, ist
            er fort — dann bleibt nur, einen neuen zu erzeugen.
          </p>
        </div>

        <div className="space-y-3 overflow-y-auto px-5 py-4">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-medium text-slate-700">Privater Schlüssel</span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(privaterSchluessel);
                  setKopiert(true);
                  window.setTimeout(() => setKopiert(false), 2000);
                }}
                className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
              >
                {kopiert ? <Check size={11} /> : <Copy size={11} />}
                {kopiert ? 'kopiert' : 'kopieren'}
              </button>
            </div>
            <pre className="max-h-40 overflow-auto rounded border border-slate-300 bg-slate-900 p-2 text-[10px] leading-tight text-slate-100">
              {privaterSchluessel}
            </pre>
          </div>

          <SchluesselAnleitung />
        </div>

        <div className="shrink-0 border-t border-slate-200 px-5 py-3">
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={verstanden}
              onChange={(e) => setVerstanden(e.target.checked)}
            />
            Ich habe den Schlüssel gesichert.
          </label>
          <button
            type="button"
            onClick={onFertig}
            disabled={!verstanden}
            className="mt-2 w-full rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-40"
          >
            Schliessen
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SshSchluesselPanel({
  userId,
  istAdmin,
}: {
  userId: string;
  istAdmin: boolean;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [neuerSchluessel, setNeuerSchluessel] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);
  const [zeigeAnleitung, setZeigeAnleitung] = useState(false);
  const [adresse, setAdresse] = useState('');
  const [notiz, setNotiz] = useState('');

  const daten = useQuery({
    queryKey: ['benutzer-schluessel', userId],
    queryFn: () => benutzerSchluessel.lesen(userId),
  });
  const alleServer = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list(),
    enabled: istAdmin,
  });

  const nach = (e: { ok: boolean; meldung: string }) => {
    setMeldung({ ok: e.ok, text: e.meldung });
    qc.invalidateQueries({ queryKey: ['benutzer-schluessel', userId] });
  };

  const erzeugen = useMutation({
    mutationFn: () => benutzerSchluessel.erzeugen(userId, name.trim()),
    onSuccess: (e) => {
      nach(e);
      if (e.privaterSchluessel) {
        setNeuerSchluessel(e.privaterSchluessel);
        setName('');
      }
    },
  });
  const widerrufen = useMutation({
    mutationFn: (id: string) => benutzerSchluessel.widerrufen(id),
    onSuccess: nach,
  });
  // Admins gleichen alle Server ab, alle anderen ihre eigenen. Derselbe
  // Vorgang, nur ein anderer Umfang -- und fuer den Knopf derselbe Text.
  const abgleich = useMutation({
    mutationFn: () =>
      istAdmin ? benutzerSchluessel.abgleich() : benutzerSchluessel.meineVerteilen(),
    onSuccess: (e) => {
      const fehler = e.ergebnisse.filter((x) => !x.ok);
      setMeldung({
        ok: fehler.length === 0,
        text:
          fehler.length === 0
            ? `Auf allen ${e.ergebnisse.length} Servern abgeglichen.`
            : `${fehler.length} von ${e.ergebnisse.length} nicht erreichbar: ${fehler
                .map((f) => f.hostname)
                .join(', ')}`,
      });
      qc.invalidateQueries({ queryKey: ['benutzer-schluessel', userId] });
    },
  });
  const ausschliessen = useMutation({
    mutationFn: (serverId: string) => benutzerSchluessel.ausschliessen(userId, serverId),
    onSuccess: nach,
  });
  const aufheben = useMutation({
    mutationFn: (serverId: string) => benutzerSchluessel.ausschlussAufheben(userId, serverId),
    onSuccess: nach,
  });
  const freigeben = useMutation({
    mutationFn: () => benutzerSchluessel.freigeben(userId, adresse.trim(), notiz.trim() || undefined),
    onSuccess: (e) => {
      setMeldung({
        ok: e.ok,
        text: e.ok
          ? `${adresse} freigegeben.`
          : `Teilweise: ${e.schritte.map((s) => `${s.name}: ${s.meldung ?? '?'}`).join(' · ')}`,
      });
      setAdresse('');
      setNotiz('');
      qc.invalidateQueries({ queryKey: ['benutzer-schluessel', userId] });
    },
  });
  const freigabeWeg = useMutation({
    mutationFn: (id: string) => benutzerSchluessel.freigabeEntfernen(userId, id),
    onSuccess: () => {
      setMeldung({ ok: true, text: 'Freigabe entfernt.' });
      qc.invalidateQueries({ queryKey: ['benutzer-schluessel', userId] });
    },
  });

  const laeuft =
    erzeugen.isPending || widerrufen.isPending || abgleich.isPending ||
    ausschliessen.isPending || aufheben.isPending || freigeben.isPending;

  const schluessel = daten.data?.schluessel ?? [];
  const aktive = schluessel.filter((s) => s.aktiv);
  const ausgeschlossen = new Set((daten.data?.ausschluesse ?? []).map((a) => a.serverId));
  const freigaben = (daten.data?.freigaben ?? []).filter((f) => f.aktiv);

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <KeyRound size={14} /> SSH-Zugang
          {aktive.length > 0 && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              {aktive.length} {aktive.length === 1 ? 'Schlüssel' : 'Schlüssel'} ·{' '}
              {daten.data?.serverMitZugang.length ?? 0} Server
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => abgleich.mutate()}
          disabled={laeuft || aktive.length === 0}
          title={
            aktive.length === 0
              ? 'Erst einen Schlüssel erzeugen'
              : istAdmin
                ? 'Schreibt auf allen Servern den Soll-Zustand. Dauert einige Sekunden je Server.'
                : 'Bringt deinen Schlüssel auf alle Server, auf die du darfst.'
          }
          className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          <RefreshCw size={11} className={abgleich.isPending ? 'animate-spin' : ''} />
          {abgleich.isPending
            ? 'verteilt …'
            : istAdmin
              ? 'Auf alle Server verteilen'
              : 'Auf meine Server verteilen'}
        </button>
      </div>

      {/* Schlüssel */}
      {schluessel.length > 0 && (
        <ul className="space-y-1">
          {schluessel.map((s: BenutzerSchluessel) => (
            <li
              key={s.id}
              className={`flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 text-[11px] ${
                s.aktiv ? 'border-slate-200' : 'border-slate-200 bg-slate-50 opacity-60'
              }`}
            >
              <span className="font-medium text-slate-800">{s.name}</span>
              <span className="truncate font-mono text-[10px] text-slate-400" title={s.fingerprint}>
                {s.fingerprint.slice(0, 26)}…
              </span>
              {s.aktiv ? (
                <span className="text-slate-500">
                  verteilt: <Zeitpunkt iso={s.zuletztVerteiltAm} />
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-rose-700">
                  <ShieldOff size={10} /> gesperrt <Zeitpunkt iso={s.widerrufenAm} />
                </span>
              )}
              {s.aktiv && istAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Schlüssel „${s.name}" sperren?\n\n`
                          + 'Er wird sofort von allen Servern entfernt. Wer ihn benutzt, kommt '
                          + 'danach nicht mehr hinein.',
                      )
                    ) {
                      widerrufen.mutate(s.id);
                    }
                  }}
                  disabled={laeuft}
                  className="ml-auto inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <Ban size={10} /> sperren
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Neuen erzeugen */}
      <div className="flex flex-wrap items-end gap-1.5">
        <label className="block flex-1">
          <span className="mb-0.5 block text-[10px] text-slate-500">
            Neuer Schlüssel — wofür?
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="MacBook, Arbeitsrechner …"
            className="w-full rounded border border-slate-300 px-2 py-1 text-[11px]"
          />
        </label>
        <button
          type="button"
          onClick={() => erzeugen.mutate()}
          disabled={laeuft || name.trim().length === 0}
          className="inline-flex items-center gap-1 rounded bg-brand-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-40"
        >
          <Plus size={11} /> {erzeugen.isPending ? 'erzeugt …' : 'erzeugen'}
        </button>
        <button
          type="button"
          onClick={() => setZeigeAnleitung((v) => !v)}
          className="rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
        >
          Anleitung
        </button>
      </div>

      {zeigeAnleitung && <SchluesselAnleitung />}

      {meldung && (
        <p className={`text-[11px] ${meldung.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
          {meldung.text}
        </p>
      )}

      {/* Nur für Admins: Server ausschliessen und Adressen freigeben */}
      {istAdmin && (
        <>
          <details className="rounded border border-slate-200">
            <summary className="cursor-pointer px-2 py-1.5 text-[11px] font-medium text-slate-700">
              <Server size={11} className="mr-1 inline" />
              Server ausschliessen
              {ausgeschlossen.size > 0 && (
                <span className="ml-1 text-amber-700">({ausgeschlossen.size})</span>
              )}
            </summary>
            <div className="border-t border-slate-100 px-2 py-1.5">
              <p className="mb-1.5 text-[10px] text-slate-500">
                Vorgabe ist Zugang zu allen. Ein Ausschluss wirkt sofort — der Schlüssel wird von
                diesem Server entfernt.
              </p>
              <div className="flex flex-wrap gap-1">
                {(alleServer.data ?? []).map((s: { id: string; hostname: string }) => {
                  const raus = ausgeschlossen.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() =>
                        raus ? aufheben.mutate(s.id) : ausschliessen.mutate(s.id)
                      }
                      disabled={laeuft}
                      title={raus ? 'Zugang wieder erlauben' : 'Von diesem Server ausschliessen'}
                      className={`rounded px-2 py-0.5 text-[10px] font-medium disabled:opacity-40 ${
                        raus
                          ? 'border border-rose-300 bg-rose-50 text-rose-700 line-through'
                          : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {s.hostname}
                    </button>
                  );
                })}
              </div>
            </div>
          </details>

          <details className="rounded border border-slate-200">
            <summary className="cursor-pointer px-2 py-1.5 text-[11px] font-medium text-slate-700">
              <Globe size={11} className="mr-1 inline" />
              Adressen für die Verwaltung freigeben
              {freigaben.length > 0 && (
                <span className="ml-1 text-slate-500">({freigaben.length})</span>
              )}
            </summary>
            <div className="border-t border-slate-100 px-2 py-1.5">
              <p className="mb-1.5 text-[10px] text-slate-500">
                Trägt die Adresse in dieselben zwei Listen ein wie bei einem neuen Server: die
                Firewall des Anbieters vor dem Verwaltungswirt und die Zugangsliste im Proxy.
              </p>
              {freigaben.length > 0 && (
                <ul className="mb-1.5 space-y-0.5">
                  {freigaben.map((f) => (
                    <li key={f.id} className="flex items-center gap-2 text-[11px]">
                      <span className="font-mono text-slate-700">{f.adresse}</span>
                      {f.notiz && <span className="text-slate-400">{f.notiz}</span>}
                      <button
                        type="button"
                        onClick={() => freigabeWeg.mutate(f.id)}
                        disabled={laeuft}
                        title="Freigabe entfernen"
                        className="ml-auto rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                      >
                        <Trash2 size={10} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap items-end gap-1.5">
                <input
                  value={adresse}
                  onChange={(e) => setAdresse(e.target.value)}
                  placeholder="203.0.113.7"
                  className="w-32 rounded border border-slate-300 px-2 py-1 font-mono text-[11px]"
                />
                <input
                  value={notiz}
                  onChange={(e) => setNotiz(e.target.value)}
                  placeholder="Büro, zu Hause …"
                  className="flex-1 rounded border border-slate-300 px-2 py-1 text-[11px]"
                />
                <button
                  type="button"
                  onClick={() => freigeben.mutate()}
                  disabled={laeuft || adresse.trim().length < 7}
                  className="rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  {freigeben.isPending ? 'trägt ein …' : 'freigeben'}
                </button>
              </div>
            </div>
          </details>
        </>
      )}

      {neuerSchluessel && (
        <SchluesselAnzeige
          privaterSchluessel={neuerSchluessel}
          onFertig={() => setNeuerSchluessel(null)}
        />
      )}
    </div>
  );
}
